import { last } from '@bugbug/core/utils/toolbox';
import { useCallback, useMemo, useState } from 'react';

import type {
  SubscriptionPlanType,
  SubscriptionBillingCycle,
} from '@bugbug/core/types/subscriptions';
import type { Maybe } from '@bugbug/core/types/utils';
import useAppRoutes from '~/hooks/useAppRoutes';
import useQueryString from '~/hooks/useQueryString';
import { selectCurrentOrganization } from '~/modules/organization/organization.selectors';
import { useAppSelector } from '~/modules/store';
import analytics from '~/services/analytics';

import { CHECKOUT_STEP, CHECKOUT_STEPS_LIST } from './CheckoutWizard.constants';

export const useCheckoutNavigation = () => {
  const queryString = useQueryString<{
    type: SubscriptionPlanType;
    billingCycle: SubscriptionBillingCycle;
    parallel: string;
    coupon?: string;
  }>();

  const appRoutes = useAppRoutes('checkout');

  const currentStepId = parseInt(appRoutes.params.stepId, 10) || CHECKOUT_STEP.BILLING_ADDRESS.id;

  const setCurrentStepId = useCallback(
    (stepId: number) => {
      appRoutes.push('checkout', { ...appRoutes.params, stepId: stepId.toString() }, queryString);
    },
    // appRoutes does not have a stable reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryString],
  );

  const handlePlanChange = useCallback(() => {
    appRoutes.push('subscription');
  }, [appRoutes]);

  const goNext = useCallback(() => {
    const confirmationStepId = CHECKOUT_STEPS_LIST.length;

    if (currentStepId < confirmationStepId) {
      if (currentStepId + 1 === CHECKOUT_STEP.PAYMENT.id) {
        analytics.trackEvent('payment_initiated');
      }
      if (currentStepId + 1 === CHECKOUT_STEP.CONFIRMATION.id) {
        analytics.trackEvent('payment_success_viewed');
      }
      setCurrentStepId(currentStepId + 1);
    }
  }, [currentStepId, setCurrentStepId]);

  const goBack = useCallback(() => {
    if (currentStepId === CHECKOUT_STEP.BILLING_ADDRESS.id) {
      handlePlanChange();
    } else {
      setCurrentStepId(currentStepId - 1);
    }
  }, [currentStepId, handlePlanChange, setCurrentStepId]);

  const result = useMemo(
    () => ({
      goNext,
      goBack,
      currentStepId,
      queryString,
    }),
    [goNext, goBack, currentStepId, queryString],
  );

  return result;
};

export const useCountryCode = () => {
  const [selectedCode, setCountryCode] = useState<Maybe<string>>(null);
  const defaultCode = (last(window.navigator.language.split('-')) ?? 'PL').toUpperCase();
  const organization = useAppSelector(selectCurrentOrganization);
  const countryCode = selectedCode ?? organization?.addresses[0]?.country ?? defaultCode;
  const value = useMemo(() => ({ countryCode, setCountryCode }), [countryCode, setCountryCode]);

  return value;
};
